import Bugsnag from '@bugsnag/js'
import {
  PaymentRequestOptions,
  PaymentRequestTokenEvent,
  PaymentRequestUpdateDetails,
} from '@stripe/stripe-js'

import { IAddressInput, IExpressPayOrderInput } from 'checkout/hooks/useApplePay'
import { PartialOrder } from 'checkout/types/IOrder'
import { t } from 'localization'

const TP = 'checkout.utils.stripePaymentOptions'

interface IGetStripePaymentRequest {
  order?: PartialOrder
  selectedCurrencyCode?: string
}

interface IGetStripeBuyData {
  order?: PartialOrder
  event: PaymentRequestTokenEvent
}

export const getStripeBuyData = ({ order, event }: IGetStripeBuyData): IExpressPayOrderInput => {
  const { payerName, payerEmail, payerPhone, shippingAddress, shippingOption, token } = event
  const orderNumber = order?.orderNumber
  const orderIdentifier = orderNumber ? orderNumber : 'guest express checkout'
  const throwError = (message: string) => {
    const error = new Error(message)
    Bugsnag.notify(error)
    throw error
  }
  if (
    !order?.finalPrice?.localizedValue ||
    Number.isNaN(order?.finalPrice?.localizedValue) ||
    !order?.finalPrice?.value ||
    Number.isNaN(order?.finalPrice?.value)
  ) {
    throwError(
      t(
        `${TP}.stripePayError`,
        'There was an issue checking out with express pay, please choose a different payment method.',
      ),
    )
  }
  if (!shippingAddress) {
    throwError(
      t(`${TP}.noShippingAddress`, {
        defaultValue:
          "No shipping address provided, can't create stripe payment for {orderIdentifier}",
        orderIdentifier,
      }),
    )
  }
  if (!shippingOption?.id) {
    throwError(
      t(`${TP}.noShippingOption`, {
        defaultValue:
          "No shipping option provided, can't create stripe payment for {orderIdentifier}",
        orderIdentifier,
      }),
    )
  }
  if (!token?.id) {
    throwError(
      t(`${TP}.noToken`, {
        defaultValue: "No stripe token, can't create stripe payment for {orderIdentifier}",
        orderIdentifier,
      }),
    )
  }
  if (window.sessionStorage.getItem('apple-pay-token') === token.id) {
    throwError(t(`${TP}.tokenAlreadyUsed`, 'Apple Pay token already used.'))
  }
  window.sessionStorage.setItem('apple-pay-token', token.id)
  const address: IAddressInput = {
    name: payerName || shippingAddress?.recipient || '',
    address1: shippingAddress?.addressLine?.shift() || token.card?.address_line1 || '',
    address2: shippingAddress?.addressLine?.shift() || token.card?.address_line2 || '',
    city: shippingAddress?.city || token.card?.address_city || '',
    countryCode: shippingAddress?.country || token.card?.address_country || '',
    postalCode: shippingAddress?.postalCode || token.card?.address_zip || '',
    phone: payerPhone || shippingAddress?.phone || '',
    state: shippingAddress?.region || token.card?.address_state || '',
  }
  const billingAddress: IAddressInput = {
    name: payerName || '',
    address1: token.card?.address_line1 || '',
    address2: token.card?.address_line2 || '',
    city: token.card?.address_city || '',
    countryCode: token.card?.address_country || '',
    postalCode: token.card?.address_zip || '',
    phone: payerPhone || '',
    state: token.card?.address_state || '',
  }
  const product = { ...order?.product }
  return {
    email: payerEmail,
    product: {
      productTemplate: {
        id: Number(product.productTemplate?.id),
      },
      size: {
        value: Number(product.size?.value),
      },
      id: Number(product.id),
    },
    isInstantShip: order?.isInstantShip,
    orderNumber,
    billingInfo: {
      name: payerName,
      orderNumber,
      stripeToken: String(token.id),
    },
    billingAddress,
    // sending USD value back here because that's what the backend expects
    finalPriceCents: order!.finalPrice!.value,
    shippingAddress: address,
    shippingServiceLevelId: shippingOption?.id!,
  }
}

export const getStripePaymentRequestOptions = ({
  order,
  selectedCurrencyCode = 'usd',
}: IGetStripePaymentRequest): PaymentRequestOptions => {
  const finalPrice = Number(order?.finalPrice?.localizedValue)
  if (Number.isNaN(finalPrice)) {
    throw Error('Invalid: no final price')
  }
  if (!order) {
    throw new Error('getStripePaymentRequestOptions must have an order object')
  }

  // UK needs to be passed as GB for Stripe to work properly
  const country =
    order.shippingAddress?.countryCode === 'UK' ? 'GB' : order.shippingAddress?.countryCode || 'US'

  return {
    // we will need a better way to determine country
    // for guest checkout
    country,
    currency: selectedCurrencyCode.toLowerCase(),
    displayItems: getStripeCheckoutDisplayItems(order),
    requestPayerName: true,
    requestPayerEmail: true,
    requestPayerPhone: true,
    requestShipping: true,
    total: {
      label: t(`${TP}.flightClub`, 'Flight Club'),
      amount: finalPrice,
    },
  }
}

export const getStripeShippingOptions = ({
  shippingServiceLevelOptions = [],
  shippingServiceLevel,
}: PartialOrder) =>
  shippingServiceLevelOptions
    .filter((option) => option.available)
    .map((option) => ({
      id: option.id,
      label: option.name,
      detail: option.description,
      amount: option?.price?.localizedValue,
    }))
    // Apple pay displays the first option as selected
    // so we sort our selected option to first
    .sort((optionA, optionB) =>
      optionA.id === shippingServiceLevel?.id
        ? -1
        : optionB.id === shippingServiceLevel?.id
        ? 1
        : 0,
    )

export const getStripeCheckoutDisplayItems = ({
  credits,
  shippingPrice,
  productPrice,
  promotion,
  taxPrice,
  finalPrice,
}: PartialOrder): PaymentRequestOptions['displayItems'] => {
  const subtotal = Number(productPrice?.localizedValue) || 0
  const shippingValue = Number(shippingPrice?.localizedValue) || 0
  const taxPriceValue = Number(taxPrice?.localizedValue) || 0
  const finalPriceNum = Number(finalPrice?.localizedValue)
  if (Number.isNaN(finalPriceNum)) {
    throw Error('Invalid: no final price')
  }
  const displayItems = [
    {
      label: t(`${TP}.subtotal`, 'Subtotal'),
      amount: subtotal,
    },
    {
      label: t(`${TP}.shippinig`, 'Shipping'),
      amount: shippingValue,
    },
    {
      label: t(`${TP}.tax`, 'Tax'),
      amount: taxPriceValue,
    },
  ]
  const promotionValue = promotion?.value.localizedValue || 0
  if (promotionValue) {
    displayItems.push({
      label: promotion?.title || t(`${TP}.promotionDiscount`, 'Promotion Discount'),
      amount: promotionValue * -1,
    })
  }
  const creditsValue = credits?.localizedValue || 0
  if (creditsValue) {
    displayItems.push({
      label: t(`${TP}.credit`, 'Credit'),
      amount: creditsValue * -1,
    })
  }
  return displayItems
}

export const getStripeDisplayOptions = (order?: PartialOrder): PaymentRequestUpdateDetails => {
  const finalPrice = Number(order?.finalPrice?.localizedValue)
  if (!order || Number.isNaN(finalPrice)) {
    throw Error(t(`${TP}.noOrder`, 'No available order'))
  }
  return {
    displayItems: getStripeCheckoutDisplayItems(order),
    shippingOptions: getStripeShippingOptions(order),
    total: {
      label: t(`${TP}.flightclub`, 'Flight Club'),
      amount: finalPrice,
    },
  }
}
